import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection, MenuTheme } from '../types.js';
import {
  ARABIC_LINE,
  bilingual,
  description,
  fitScale,
  hasTitle,
  image,
  lineCount,
  listCss,
  listItem,
  listItemHeight,
  priceBlock,
  sectionSubtitle,
  sectionTitle,
  type MenuFamily,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Stack Panels: tall panels edge to edge in alternating fills (black, near-black, the hot
 * colour, cream), condensed capitals, prices set flush right and an outlined call-out box at the
 * foot of a section. This file holds the family's shared pieces as well as the five-panel design;
 * the trio and bands designs import them, each under its own class prefix.
 *
 * The call-out box is board data: the last item of a section, when it carries a badge, is drawn
 * as the outlined "MAKE IT A DOUBLE  +1 JD" box instead of a plain line, with the badge as its tag.
 */

/* ------------------------------------------------------------------ shared look */

export const SP_THEME: MenuTheme = {
  backgroundColor: '#0d0d0d',
  surfaceColor: '#1a1a1a',
  textColor: '#ffffff',
  primaryColor: '#f26a1c',
  accentColor: '#f2e6d0',
  fontId: 'cafe',
  backgroundMediaId: null,
};

export const SP_VARIANTS: MenuStyleVariant[] = [
  { id: 'black-orange', name: 'Black & orange', theme: {} },
  { id: 'black-red', name: 'Black & red', theme: { backgroundColor: '#0e0c0c', surfaceColor: '#1c1818', primaryColor: '#d8281e', accentColor: '#f5ebdc' } },
  { id: 'navy-yellow', name: 'Navy & yellow', theme: { backgroundColor: '#0d182b', surfaceColor: '#15233d', primaryColor: '#f7b928', accentColor: '#f4eedf' } },
  { id: 'green-cream', name: 'Green & cream', theme: { backgroundColor: '#0f241a', surfaceColor: '#173123', textColor: '#fff8ea', primaryColor: '#e9a23b', accentColor: '#f3e8cf' } },
];

export const spFamily = (layout: string): MenuFamily => ({ id: 'stack-panels', name: 'Stack Panels', layout });

/** Panel fills in the order they cycle: near-black, the hot colour, cream, black. */
export const SP_KINDS = ['dark', 'hot', 'cream', 'black'] as const;
export type SpKind = (typeof SP_KINDS)[number];

/** Ids of the items drawn as call-out boxes: the last of each section, when it has a badge. */
export function calloutIds(board: MenuBoardContent, exclude?: string | null): Set<string> {
  const ids = new Set<string>();
  for (const group of groupBySection(board, exclude)) {
    const last = group.items[group.items.length - 1];
    if (last && (last.badge?.trim() || last.badgeAr?.trim())) ids.add(last.id);
  }
  return ids;
}

/** The hero photo: the first decoration picture, or failing that the first item photo. */
export function heroPhotoId(board: MenuBoardContent): string | null {
  return board.decorMediaIds[0] || board.items.find((i) => i.imageMediaId)?.imageMediaId || null;
}

export const BURGER_ICON =
  '<svg viewBox="0 0 48 40" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M6 17c0-8 8-13 18-13s18 5 18 13z"/><path d="M4 24c4-3 8 3 12 0s8 3 12 0 8 3 12 0 4 0 4 0"/>' +
  '<path d="M6 30h36v1a5 5 0 0 1-5 5H11a5 5 0 0 1-5-5z"/><path d="M17 10.5h.01M24 8.5h.01M31 10.5h.01"/></svg>';

/** Logo, or the brand name between two burger marks; the board title when there is no brand. */
export function spBrand(ctx: RenderContext, p: string): string {
  const { board } = ctx;
  const logo = image(ctx, board.brand.logoMediaId, `${p}-logo`);
  const nameEn = board.brand.name?.trim() || null;
  const nameAr = board.brand.nameAr?.trim() || null;
  const en = nameEn || nameAr ? nameEn : board.settings.title ?? board.name;
  const ar = nameEn || nameAr ? nameAr : board.settings.titleAr;
  const scale = Math.min(fitScale(en, 9, 0.5), fitScale(ar, 12, 0.5));
  const name = bilingual(ctx, en, ar, `${p}-brand`);
  if (!logo && !name) return '';
  return [
    `<div class="${p}-brandblock">`,
    logo,
    name
      ? `<div class="${p}-lockup"><span class="${p}-icon">${BURGER_ICON}</span><div class="${p}-names" style="font-size:${scale.toFixed(3)}em">${name}</div><span class="${p}-icon">${BURGER_ICON}</span></div>`
      : '',
    '</div>',
  ].join('');
}

/** The tagline as the big two-line slogan. */
export function spSlogan(ctx: RenderContext, p: string, maxChars = 30): string {
  const { brand } = ctx.board;
  const scale = Math.min(fitScale(brand.tagline, maxChars, 0.6), fitScale(brand.taglineAr, maxChars * 1.2, 0.6));
  const text = bilingual(ctx, brand.tagline, brand.taglineAr, `${p}-slogan`);
  return text ? `<div class="${p}-sloganbox" style="font-size:${scale.toFixed(3)}em">${text}</div>` : '';
}

export function spCallout(ctx: RenderContext, item: MenuItemContent, p: string): string {
  const label = (ctx.showEn ? item.badge || item.badgeAr : item.badgeAr || item.badge)?.trim();
  return [
    `<div class="${p}-call">`,
    label ? `<span class="${p}-call-tag" dir="auto">${escapeHtml(label)}</span>` : '',
    `<div class="${p}-call-text">${bilingual(ctx, item.title, item.titleAr, `${p}-call-name`)}${description(ctx, item, `${p}-call-desc`)}</div>`,
    `<div class="${p}-call-price">${priceBlock(ctx, item, p)}</div>`,
    '</div>',
  ].join('');
}

export function spTitleScale(section: MenuSection, maxChars: number): number {
  return Math.min(fitScale(section.title, maxChars, 0.55), fitScale(section.titleAr, maxChars * 1.3, 0.55));
}

/** One item line; an item with several sizes may drop them onto a line of their own. */
export function spLine(ctx: RenderContext, item: MenuItemContent, p: string): string {
  const line = listItem(ctx, item, p);
  return item.variants.length > 1 ? `<div class="${p}-multi">${line}</div>` : line;
}

/** Heading, short rule and subtitle. */
export function spHead(ctx: RenderContext, b: MenuBlock, p: string, maxChars: number): string {
  if (!hasTitle(b.section)) return '';
  return [
    `<div class="${p}-head">`,
    `<div style="font-size:${spTitleScale(b.section, maxChars).toFixed(3)}em">${sectionTitle(ctx, b.section, `${p}-title`)}</div>`,
    `<span class="${p}-rule"></span>`,
    b.continued ? '' : sectionSubtitle(ctx, b.section, `${p}-sub`),
    '</div>',
  ].join('');
}

/** A section (or the part of one) as a stack in a tall panel: heading, lines, photo, call-out. */
export function spStack(ctx: RenderContext, b: MenuBlock, callouts: Set<string>, p: string, maxTitle: number): string {
  const callout = b.items.find((i) => callouts.has(i.id));
  const rows = b.items
    .filter((i) => i !== callout)
    .map((i) => spLine(ctx, i, p))
    .join('');
  const photo = b.continued ? '' : image(ctx, b.section.imageMediaId, `${p}-ph-img`);
  return [
    `<div class="${p}-sec">`,
    spHead(ctx, b, p, maxTitle),
    rows ? `<div class="${p}-rows">${rows}</div>` : '',
    photo ? `<div class="${p}-ph">${photo}</div>` : '',
    callout ? spCallout(ctx, callout, p) : '',
    '</div>',
  ].join('');
}

/* ------------------------------------------------------------------ shared costs */

export interface SpMetrics {
  /** Height of a heading line, its rule and the space under it. */
  head: number;
  /** Characters of an item name that fit on one line beside its price. */
  nameChars: number;
  item: { nameEm: number; descEm: number; perLine: number; gap: number };
}

/** Height of one item line in em, including a wrapped name and sizes pushed to their own line. */
export function spItemHeight(board: MenuBoardContent, item: MenuItemContent, m: SpMetrics): number {
  const en = board.settings.language !== 'ar';
  const wraps = (en ? item.title : item.titleAr || item.title).trim().length > m.nameChars;
  return (
    listItemHeight(board, item, m.item) +
    (wraps ? m.item.nameEm * 1.15 : 0) +
    (item.variants.length > 1 ? m.item.nameEm * 1.15 : 0)
  );
}

/** Height of a call-out box in em. */
export function spCalloutHeight(board: MenuBoardContent, item: MenuItemContent, perLine: number): number {
  const lang = board.settings.language;
  const both = lang === 'both' && item.titleAr;
  const desc = board.settings.showDescriptions
    ? (lang !== 'ar' || !item.descriptionAr ? lineCount(item.description, perLine, 2) : 0) +
      (lang !== 'en' ? lineCount(item.descriptionAr, perLine, 2) : 0)
    : 0;
  return 2.4 + 1.45 * 1.2 + (both ? 1.15 * 1.2 * ARABIC_LINE : 0) + desc * 0.8 * 1.3 + 0.8;
}

/** Height of a heading block in em. */
export function spHeadHeight(board: MenuBoardContent, section: MenuSection, continued: boolean, head: number): number {
  if (!hasTitle(section)) return 0;
  const lang = board.settings.language;
  const both = lang === 'both';
  const sub = continued
    ? 0
    : (section.subtitle && lang !== 'ar' ? 1.3 : 0) + (section.subtitleAr && lang !== 'en' ? 1.3 * ARABIC_LINE : 0) +
      (lang === 'ar' && !section.subtitleAr && section.subtitle ? 1.3 : 0);
  return head + (both && section.titleAr && section.title ? head * 0.55 * ARABIC_LINE : 0) + sub;
}

/** A tall panel: headings, lines, an optional section photo and call-out. */
export function spStackColumn(
  board: MenuBoardContent,
  callouts: Set<string>,
  m: SpMetrics & { capacity: number; photo: number },
): MenuColumn {
  return {
    capacity: m.capacity,
    blockCost: (section, items, continued) =>
      spHeadHeight(board, section, continued, m.head) +
      items.reduce(
        (sum, item) => sum + (callouts.has(item.id) ? spCalloutHeight(board, item, m.item.perLine) : spItemHeight(board, item, m)),
        0,
      ) +
      (!continued && section.imageMediaId ? m.photo : 0) +
      2.2,
  };
}

/* ------------------------------------------------------------------ shared css */

/**
 * Panel fills, headings, item lines and call-outs under `S` with prefix `p`. Each panel kind sets
 * its own ink through `--<p>-*` properties, so one set of rules serves every fill.
 */
export function spCss(S: string, p: string): string {
  return `
${S}{--${p}-on-hot:var(--mb-text);--${p}-cream-head:var(--mb-primary);padding:0;background:var(--mb-bg)}
${S}.mb-v-navy-yellow,${S}.mb-v-green-cream{--${p}-on-hot:var(--mb-bg);--${p}-cream-head:var(--mb-bg)}
${S} .${p}-k-black{--${p}-fill:var(--mb-bg);--${p}-head:var(--mb-primary);--${p}-ink:var(--mb-text);--${p}-price:var(--mb-primary)}
${S} .${p}-k-dark{--${p}-fill:var(--mb-surface);--${p}-head:var(--mb-primary);--${p}-ink:var(--mb-text);--${p}-price:var(--mb-primary)}
${S} .${p}-k-hot{--${p}-fill:var(--mb-primary);--${p}-head:var(--${p}-on-hot);--${p}-ink:var(--${p}-on-hot);--${p}-price:var(--${p}-on-hot)}
${S} .${p}-k-cream{--${p}-fill:var(--mb-accent);--${p}-head:var(--${p}-cream-head);--${p}-ink:var(--mb-bg);--${p}-price:var(--${p}-cream-head)}
${S} .${p}-k-black,${S} .${p}-k-dark,${S} .${p}-k-hot,${S} .${p}-k-cream{background:var(--${p}-fill);color:var(--${p}-ink)}
${S} .${p}-brandblock{display:flex;flex-direction:column;align-items:center;gap:.6em;text-align:center;color:var(--mb-text)}
${S} .${p}-logo{max-height:6em;max-width:80%;object-fit:contain}
${S} .${p}-lockup{display:flex;align-items:center;justify-content:center;gap:.7em;max-width:100%}
${S} .${p}-icon{flex:0 0 auto;width:3.2em;color:var(--mb-primary);display:flex}
${S} .${p}-icon svg{width:100%;height:auto}
${S} .${p}-names{min-width:0;width:min-content;max-width:100%}
${S} .${p}-brand{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.6em;line-height:.95;
  text-transform:uppercase;letter-spacing:.04em;color:var(--mb-text)}
${S} .${p}-brand.mb-2nd{font-size:2em;margin-top:.15em}
${S} .${p}-slogan{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.3em;line-height:1.02;
  text-transform:uppercase;color:var(--mb-primary)}
${S} .${p}-slogan.mb-2nd{font-size:2em;margin-top:.2em}
${S} .${p}-head{margin-bottom:1.3em}
${S} .${p}-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3em;line-height:1.05;
  text-transform:uppercase;letter-spacing:.02em;color:var(--${p}-head);white-space:nowrap}
${S} .${p}-title.mb-2nd{font-size:1.75em}
${S} .${p}-rule{display:block;width:3.2em;height:.22em;background:var(--${p}-head);margin:.45em 0 .5em;opacity:.9}
${S} .${p}-sub{font-size:.95em;font-weight:700;letter-spacing:.14em;text-transform:uppercase;opacity:.85}
${S} .${p}-sub.mb-ar{letter-spacing:0}
${listCss(S, p, { name: '1.45em', price: '1.45em', desc: '.85em', leader: '0 solid transparent' })}
${S} .${p}-item{margin-bottom:1.15em}
${S} .${p}-multi .${p}-line{flex-wrap:wrap;row-gap:.1em}
${S} .${p}-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;letter-spacing:.02em;color:var(--${p}-ink)}
${S} .${p}-name2{font-family:var(--mb-font-heading);font-size:1.15em;opacity:1;color:var(--${p}-ink)}
${S} .${p}-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--${p}-price)}
${S} .${p}-sizes{margin-inline-start:auto;flex-wrap:wrap;justify-content:flex-end;row-gap:0}
${S} .${p}-size small{font-weight:700;text-transform:uppercase;letter-spacing:.05em}
${S} .${p}-old{color:var(--${p}-ink)}
${S} .${p}-badge{font-family:var(--mb-font-heading);font-weight:400;letter-spacing:.05em;text-transform:uppercase;background:var(--${p}-head);color:var(--${p}-fill);border-radius:.2em}
${S} .${p}-desc{max-width:24em;opacity:.72}
${S} .${p}-call{position:relative;display:flex;align-items:center;gap:1em;border:.14em solid var(--${p}-head);padding:1em 1.2em;margin-top:.6em}
${S} .${p}-call-tag{position:absolute;top:0;inset-inline-start:.9em;transform:translateY(-58%);background:var(--${p}-fill);color:var(--${p}-head);
  padding:0 .5em;font-size:.75em;font-weight:800;letter-spacing:.16em;text-transform:uppercase;white-space:nowrap}
${S} .${p}-call-text{flex:1;min-width:0}
${S} .${p}-call-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.45em;line-height:1.15;
  text-transform:uppercase;color:var(--${p}-ink)}
${S} .${p}-call-name.mb-2nd{font-size:1.15em}
${S} .${p}-call-desc{font-size:.8em;opacity:.75;margin-top:.15em;text-transform:uppercase;letter-spacing:.06em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .${p}-call-price{flex-shrink:0;font-size:1.25em}
${S} .${p}-call-price .${p}-price,${S} .${p}-call-price .${p}-pricebox{color:var(--${p}-head)}
${S} .${p}-call-price .${p}-price:before{content:'+'}
${S} .${p}-call-price .${p}-old:before{content:none}
${S} .${p}-ph{position:relative;overflow:hidden}
${S} .${p}-ph-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}
`;
}

/**
 * A tall panel holding sections: separators between them, the last one stretched so its photo
 * fills the room left and its call-out sits at the foot. Padding comes from `--<p>-px`
 * (sides), `--<p>-pt` (top) and `--<p>-pb` (bottom), so photos can bleed to the edges.
 */
export function spPanelCss(S: string, p: string): string {
  return `
${S} .${p}-panel{--${p}-px:2em;--${p}-pt:3em;--${p}-pb:2.6em;position:relative;min-width:0;min-height:0;overflow:hidden;
  display:flex;flex-direction:column;padding:var(--${p}-pt) var(--${p}-px) var(--${p}-pb)}
${S}.mb-portrait .${p}-panel{--${p}-px:2.4em;--${p}-pt:2.6em;--${p}-pb:2.2em}
${S} .${p}-sec{display:flex;flex-direction:column;flex:none}
${S} .${p}-sec+.${p}-sec{margin-top:1.6em;padding-top:1.8em;border-top:.12em solid color-mix(in srgb,var(--${p}-ink) 25%,transparent)}
${S} .${p}-sec:last-child{flex:1 1 auto;min-height:0}
${S} .${p}-sec:last-child .${p}-call{margin-top:auto}
${S} .${p}-ph{height:13em;margin:.4em calc(-1 * var(--${p}-px)) 1em;flex:none;
  -webkit-mask-image:linear-gradient(transparent,#000 22%);mask-image:linear-gradient(transparent,#000 22%)}
${S} .${p}-sec:last-child .${p}-ph{flex:1 1 13em;height:auto;min-height:8em}
${S}.mb-portrait .${p}-ph{height:8em}
${S}.mb-portrait .${p}-sec:last-child .${p}-ph{flex-basis:8em;min-height:6em}
${S} .${p}-sec:last-child .${p}-ph:last-child{margin-bottom:calc(-1 * var(--${p}-pb))}
${S} .${p}-k-hot .${p}-ph,${S} .${p}-k-cream .${p}-ph{-webkit-mask-image:none;mask-image:none;margin-inline:0;
  outline:.18em solid var(--${p}-head);outline-offset:.45em;margin-top:1em;margin-bottom:1.6em}
${S} .${p}-k-hot .${p}-sec:last-child .${p}-ph:last-child,${S} .${p}-k-cream .${p}-sec:last-child .${p}-ph:last-child{margin-bottom:.5em}
`;
}

/* ------------------------------------------------------------------ five panels */

const ID = 'stack-panels-five';
const S = `.mb-t-${ID}`;
const P = 'sp5';

function columns(board: MenuBoardContent, portrait: boolean, callouts: Set<string>): MenuColumn[] {
  const column = spStackColumn(board, callouts, {
    capacity: portrait ? 36 : 61,
    photo: portrait ? 6 : 14,
    head: 5.2,
    nameChars: portrait ? 26 : 15,
    item: { nameEm: 1.45, descEm: 0.85, perLine: portrait ? 52 : 34, gap: 1.15 },
  });
  return [column, column, column, column];
}

export const stackPanelsFive: MenuTemplate = {
  id: ID,
  name: 'Stack panels: five',
  description:
    'Five tall panels edge to edge in black, orange and cream: your brand, a big photo and slogan, then one panel per section with call-out boxes.',
  defaultTheme: SP_THEME,
  variants: SP_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: spFamily('Five panels'),
  uses: { featured: false, sectionImages: true, brand: true, decorations: ['Hero photo in the first panel'] },

  paginate(board, orientation) {
    const callouts = calloutIds(board);
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait', callouts), { balance: true });
  },

  render(ctx) {
    const callouts = calloutIds(ctx.board);
    const filled = ctx.page.columns.filter((c) => c.length > 0);
    const panels = filled.map(
      (col, i) =>
        `<div class="${P}-panel ${P}-k-${SP_KINDS[i % SP_KINDS.length]}">${col.map((b) => spStack(ctx, b, callouts, P, 11)).join('')}</div>`,
    );
    if (panels.length === 0) panels.push(`<div class="${P}-panel ${P}-k-dark"><div class="mb-empty">—</div></div>`);
    const photo = image(ctx, heroPhotoId(ctx.board), `${P}-hero-img`);
    return [
      `<div class="${P}-wrap">`,
      `<div class="${P}-hero ${P}-k-black">`,
      spBrand(ctx, P),
      photo ? `<div class="${P}-hero-ph">${photo}</div>` : `<div class="${P}-hero-gap">${BURGER_ICON}</div>`,
      spSlogan(ctx, P, 24),
      '</div>',
      `<div class="${P}-panels ${P}-n${panels.length}">${panels.join('')}</div>`,
      '</div>',
    ].join('');
  },

  css: `
${spCss(S, P)}
${S} .${P}-wrap{flex:1;min-height:0;display:grid;grid-template-columns:25fr 75fr}
${S}.mb-portrait .${P}-wrap{grid-template-columns:1fr;grid-template-rows:30fr 70fr}
${S} .${P}-hero{position:relative;min-width:0;min-height:0;overflow:hidden;display:flex;flex-direction:column;padding:3em 2.4em 3.2em;gap:1em}
${S} .${P}-hero-ph{position:relative;flex:1;min-height:0;margin:0 -2.4em}
${S} .${P}-hero-gap{flex:1;min-height:0;display:flex;align-items:center;justify-content:center;color:var(--mb-primary);opacity:.22}
${S} .${P}-hero-gap svg{width:62%;max-height:80%}
${S} .${P}-hero-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;
  -webkit-mask-image:linear-gradient(transparent,#000 18%,#000 80%,transparent);mask-image:linear-gradient(transparent,#000 18%,#000 80%,transparent)}
${S} .${P}-sloganbox{position:relative}
${S}.mb-portrait .${P}-hero{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:auto 1fr;padding:3em 3em 2.6em;gap:1.5em 2em}
${S}.mb-portrait .${P}-brandblock{grid-column:1;grid-row:1;align-items:flex-start;text-align:start}
${S}.mb-portrait .${P}-lockup{justify-content:flex-start}
${S}.mb-portrait .${P}-sloganbox{grid-column:1;grid-row:2;align-self:end}
${S}.mb-portrait .${P}-hero-ph,${S}.mb-portrait .${P}-hero-gap{grid-column:2;grid-row:1 / span 2;margin:-3em -3em -2.6em 0}
${S}.mb-portrait[dir=rtl] .${P}-hero-ph{margin:-3em 0 -2.6em -3em}
${S}.mb-portrait .${P}-hero-img{-webkit-mask-image:linear-gradient(to right,transparent,#000 30%);mask-image:linear-gradient(to right,transparent,#000 30%)}
${S}.mb-portrait[dir=rtl] .${P}-hero-img{-webkit-mask-image:linear-gradient(to left,transparent,#000 30%);mask-image:linear-gradient(to left,transparent,#000 30%)}
${S} .${P}-panels{min-width:0;min-height:0;display:grid;grid-auto-flow:column;grid-auto-columns:1fr}
${S}.mb-portrait .${P}-panels{grid-auto-flow:row;grid-template-columns:1fr 1fr;grid-auto-rows:1fr;grid-auto-columns:auto}
${S}.mb-portrait .${P}-n1,${S}.mb-portrait .${P}-n3 .${P}-panel:last-child{grid-column:1 / -1}
${S}.mb-portrait .${P}-n1{grid-template-columns:1fr}
${spPanelCss(S, P)}
`,
};
